import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { useAuth } from '../contexts/AuthContext'
import { teamApiService, type PendingInvite } from '../services/teamApiService'
import { ApiError } from '../services/BaseApiService'
import { ROLE_LABELS, SecurityRole } from '../constants'
import { colors, fontSize, radius, shadow, spacing } from '../styles/theme'


/**
 * Where a signed-in account on no team lands — it left its last team (V085). It keeps its login and can accept an
 * invitation waiting for it, or create a team of its own (and become its Team Admin). Uses only endpoints that need
 * no team, so nothing here 401s the account back to the login page.
 */
export default function NoTeamPage() {
  const { user, refresh, logout } = useAuth()
  const navigate = useNavigate()
  const [invite, setInvite] = useState<PendingInvite | null>(null)
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    teamApiService.pendingInvite().then(r => setInvite(r.invite)).catch(() => setInvite(null))
  }, [])

  async function enter(action: () => Promise<unknown>) {
    setBusy(true); setError('')
    try {
      await action()
      await refresh()
      navigate('/app/dashboard', { replace: true })
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.')
    } finally {
      setBusy(false)
    }
  }

  async function decline() {
    setBusy(true); setError('')
    try { await teamApiService.declineInvite(); setInvite(null) }
    catch (err) { setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.') }
    finally { setBusy(false) }
  }

  const card: React.CSSProperties = {
    background: colors.surface, borderRadius: radius.lg, boxShadow: shadow.lg, padding: spacing.xl,
    width: '100%', maxWidth: 520, boxSizing: 'border-box',
  }
  const primary: React.CSSProperties = {
    padding: `${spacing.sm} ${spacing.md}`, background: colors.primary, color: '#fff', border: 'none',
    borderRadius: radius.sm, cursor: 'pointer', fontWeight: 600, opacity: busy ? 0.6 : 1,
  }
  const secondary: React.CSSProperties = {
    padding: `${spacing.sm} ${spacing.md}`, background: 'transparent', color: colors.textSecondary,
    border: `1px solid ${colors.border}`, borderRadius: radius.sm, cursor: 'pointer',
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: colors.background, padding: spacing.md }}>
      <div style={card}>
        <h1 style={{ fontSize: fontSize.xl, fontWeight: 700, color: colors.text, margin: 0, marginBottom: spacing.sm }}>
          You're not on a team
        </h1>
        <p style={{ fontSize: fontSize.sm, color: colors.textSecondary, marginTop: 0, marginBottom: spacing.lg }}>
          {user?.email ? <>Your account <strong>{user.email}</strong> is still active, but it doesn't belong to a team. </> : null}
          Accept an invitation, or create a team of your own.
        </p>

        {error && (
          <div role="alert" style={{ background: colors.errorLight, color: colors.error, border: `1px solid ${colors.errorBorder}`, borderRadius: radius.sm, padding: `${spacing.sm} ${spacing.md}`, fontSize: fontSize.sm, marginBottom: spacing.md }}>
            {error}
          </div>
        )}

        {invite && (
          <div style={{ border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: spacing.md, marginBottom: spacing.lg }}>
            <div style={{ fontWeight: 600, color: colors.text, marginBottom: spacing.xs }}>
              Invitation to join {invite.teamName}
            </div>
            <div style={{ fontSize: fontSize.sm, color: colors.textSecondary, marginBottom: spacing.md }}>
              As {ROLE_LABELS[invite.role] ?? 'a member'} · expires {new Date(invite.expiresAt).toLocaleDateString()}
            </div>
            <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap' }}>
              <button disabled={busy} style={primary} onClick={() => enter(() => teamApiService.acceptInvite())}>Accept and join</button>
              <button disabled={busy} style={secondary} onClick={decline}>Decline</button>
            </div>
          </div>
        )}

        <form onSubmit={e => { e.preventDefault(); if (name.trim()) enter(() => teamApiService.create(name.trim())) }}>
          <label htmlFor="new-team-name" style={{ display: 'block', fontSize: fontSize.sm, fontWeight: 500, marginBottom: spacing.xs, color: colors.text }}>
            Create a team
          </label>
          <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap' }}>
            <input id="new-team-name" value={name} onChange={e => setName(e.target.value)} placeholder="Team name" maxLength={255}
              style={{ flex: '1 1 200px', minWidth: 0, padding: '8px 10px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: fontSize.base, color: colors.text }} />
            <button type="submit" disabled={busy || !name.trim()} style={{ ...primary, opacity: busy || !name.trim() ? 0.6 : 1 }}>Create team</button>
          </div>
          <p style={{ fontSize: fontSize.xs, color: colors.textMuted, marginBottom: 0 }}>
            You'll be its Team Admin. A new team is on the Standard plan and needs a card before it can render.
          </p>
        </form>

        <div style={{ marginTop: spacing.lg }}>
          <button style={secondary} onClick={async () => { await logout(); navigate('/login') }}>Sign out</button>
        </div>
      </div>
    </div>
  )
}
